import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { AutoTextarea, formatEuro } from "@/components/AutoTextarea";
import { EuroInput } from "@/components/EuroInput";
import { Package, Loader2, Save, Plus, Trash2 } from "lucide-react";

const inputCls = "h-10 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-3 text-zinc-50 focus:border-[#00E575] outline-none text-sm";
const textareaCls = "bg-[#09090B] border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 focus:border-[#00E575] outline-none text-sm";

export default function AdminPackages() {
  const [packages, setPackages] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/price-packages"), api.get("/heat-pump-products")])
      .then(([p, pr]) => { setPackages(p.data); setProducts(pr.data); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const updItem = (pkgId, itemId, field, value) => {
    setPackages(packages.map((p) => p.id === pkgId ? { ...p, items: p.items.map((i) => i.id === itemId ? { ...i, [field]: value } : i) } : p));
  };

  const removeItem = (pkgId, itemId) => {
    setPackages(packages.map((p) => p.id === pkgId ? { ...p, items: p.items.filter((i) => i.id !== itemId) } : p));
  };

  const addItem = (pkgId) => {
    setPackages(packages.map((p) => p.id === pkgId ? { ...p, items: [...p.items, { id: `new-${Date.now()}`, title: "Neue Position", description: "", quantity: 1, unit_price_net: 0, vat_rate: 0.19 }] } : p));
  };

  const updPkg = (pkgId, field, value) => {
    setPackages(packages.map((p) => p.id === pkgId ? { ...p, [field]: value } : p));
  };

  const save = async (pkg) => {
    setSavingId(pkg.id);
    try {
      const cleanItems = pkg.items.map((i) => ({
        title: i.title,
        description: i.description || "",
        quantity: Number(i.quantity) || 1,
        unit_price_net: Number(i.unit_price_net) || 0,
        vat_rate: Number(i.vat_rate) || 0.19,
        optional: !!i.optional,
      }));
      await api.patch(`/admin/price-packages/${pkg.id}`, {
        name: pkg.name,
        heat_pump_product_id: pkg.heat_pump_product_id,
        description: pkg.description || "",
        power_class: pkg.power_class,
        category: pkg.category || "guenstig",
        base_price_net: pkg.base_price_net || 0,
        active: pkg.active,
        items: cleanItems,
      });
      toast.success(`${pkg.name} gespeichert`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <AppHeader title="WP-Pakete" back="/dashboard" />
      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-2xl bg-[#00E575]/15 flex items-center justify-center"><Package className="text-[#00E575]" /></div>
          <div>
            <h1 className="font-display text-2xl font-semibold">WP-Pakete & Preise</h1>
            <p className="text-sm text-zinc-300 font-medium">Wärmepumpen-Pakete und Angebotspositionen verwalten</p>
          </div>
        </div>

        {loading ? <div className="text-zinc-300 text-center font-medium py-12"><Loader2 className="animate-spin inline mr-2" /> Lade…</div> : (
          <div className="space-y-4">
            {packages.map((pkg) => (
              <div key={pkg.id} data-testid={`admin-pkg-${pkg.id}`} className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5">
                {/* Zeile 1: Paketname (volle Breite, mehrzeilig) */}
                <div className="mb-3">
                  <label className="block text-[12px] uppercase tracking-widest text-zinc-300 font-semibold mb-1">Paketname</label>
                  <AutoTextarea
                    className={textareaCls}
                    value={pkg.name}
                    onChange={(e) => updPkg(pkg.id, "name", e.target.value)}
                    testid={`admin-pkg-${pkg.id}-name`}
                  />
                </div>
                {/* Zeile 2: Leistungsklasse + WP-Produkt nebeneinander */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                  <div>
                    <label className="block text-[12px] uppercase tracking-widest text-zinc-300 font-semibold mb-1">Leistungsklasse</label>
                    <AutoTextarea
                      className={textareaCls}
                      value={pkg.power_class}
                      onChange={(e) => updPkg(pkg.id, "power_class", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] uppercase tracking-widest text-zinc-300 font-semibold mb-1">Kategorie</label>
                    <select
                      className={inputCls}
                      value={pkg.category || "guenstig"}
                      onChange={(e) => updPkg(pkg.id, "category", e.target.value)}
                      data-testid={`admin-pkg-${pkg.id}-category`}
                    >
                      <option value="guenstig">Günstig</option>
                      <option value="komfort">Komfort</option>
                      <option value="premium">Premium</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[12px] uppercase tracking-widest text-zinc-300 font-semibold mb-1">WP-Produkt</label>
                    <select className={inputCls} value={pkg.heat_pump_product_id} onChange={(e) => updPkg(pkg.id, "heat_pump_product_id", e.target.value)}>
                      {products.map((pr) => <option key={pr.id} value={pr.id}>{pr.model} ({pr.power_class})</option>)}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  {pkg.items.map((it) => (
                    <div key={it.id} className="bg-[#09090B] p-3 rounded-xl space-y-2 border border-zinc-800">
                      {/* Titel (auto-grow) */}
                      <div>
                        <label className="block text-[12px] uppercase tracking-widest text-zinc-300 font-semibold mb-1">Titel</label>
                        <AutoTextarea
                          className="bg-transparent border border-zinc-700 rounded-lg text-sm font-medium px-2.5 py-2 focus:border-[#00E575]"
                          value={it.title}
                          onChange={(e) => updItem(pkg.id, it.id, "title", e.target.value)}
                          placeholder="Titel"
                        />
                      </div>
                      {/* Beschreibung (auto-grow) */}
                      <div>
                        <label className="block text-[12px] uppercase tracking-widest text-zinc-300 font-semibold mb-1">Beschreibung</label>
                        <AutoTextarea
                          className="bg-transparent border border-zinc-700 rounded-lg text-sm text-zinc-300 px-2.5 py-2 focus:border-[#00E575]"
                          value={it.description || ""}
                          onChange={(e) => updItem(pkg.id, it.id, "description", e.target.value)}
                          placeholder="z. B. Außeneinheit, Speicher 200 l"
                        />
                      </div>
                      {/* Preis + Löschen nebeneinander */}
                      <div className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-10 sm:col-span-11">
                          <label className="block text-[12px] uppercase tracking-widest text-zinc-300 font-semibold mb-1">Preis netto</label>
                          <EuroInput
                            value={it.unit_price_net}
                            onChange={(n) => updItem(pkg.id, it.id, "unit_price_net", n)}
                            className="h-10 w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 text-sm font-mono focus:border-[#00E575] outline-none"
                          />
                        </div>
                        <div className="col-span-2 sm:col-span-1 flex items-end">
                          <button
                            onClick={() => removeItem(pkg.id, it.id)}
                            className="h-10 w-full rounded-lg bg-zinc-800 hover:bg-red-500/20 hover:text-red-400 text-zinc-400 flex items-center justify-center transition"
                            aria-label="Position entfernen"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button onClick={() => addItem(pkg.id)} className="text-sm text-[#00E575] hover:underline inline-flex items-center gap-1 mt-2"><Plus size={14} /> Position hinzufügen</button>
                </div>

                {/* Summen-Vorschau */}
                {pkg.items.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-zinc-800 flex items-center justify-between text-sm">
                    <span className="text-zinc-400">Paket-Summe (netto)</span>
                    <span className="font-mono text-[#00E575] font-bold">
                      {formatEuro(pkg.items.reduce((s, i) => s + (Number(i.unit_price_net) || 0) * (Number(i.quantity) || 1), 0))} €
                    </span>
                  </div>
                )}

                <div className="flex justify-end mt-4">
                  <button
                    data-testid={`admin-pkg-save-${pkg.id}`}
                    onClick={() => save(pkg)}
                    disabled={savingId === pkg.id}
                    className="h-11 px-5 rounded-xl bg-[#00E575] text-[#09090B] font-semibold inline-flex items-center gap-1.5 hover:bg-[#00C964]"
                  >
                    {savingId === pkg.id ? <Loader2 className="animate-spin" size={16} /> : <><Save size={16} /> Speichern</>}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
